import {
  CASTLE_BK,
  CASTLE_BQ,
  CASTLE_WK,
  CASTLE_WQ,
  COLOR_MASK,
  type Color,
  FLAG_ACTION,
  FLAG_CAPTURE,
  KING,
  PAWN,
  QUEEN,
  ROOK,
  TYPE_MASK,
  WHITE,
  colorBit,
  encodeMove,
  moveFrom,
  movePromo,
  moveTo,
  onBoard,
  pieceColor,
  squareName,
} from '../board.ts';
import { pieceDef } from '../pieces.ts';
import { FenError } from '../notation.ts';
import { type MoveContext, draw } from '../rules.ts';
import type { Position, Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 18 (draft). Every piece is grey. Both armies look the same, so you have to remember
// which pieces are yours, and nothing stops you moving one of your opponent's (a bluff). On
// their turn, instead of moving, your opponent may challenge your last move: if the piece
// wasn't yours, the move is taken back and you lose your turn; if it was, they lose theirs
// and you move again. There's no check: capture the king to win. Taking a king with a bluff
// is always caught. No en passant; a pawn reaching the far row becomes a queen.
//
// Every move is an action (the engine's piece rules assume you move your own pieces): payload
// 0 is a piece move, CASTLE a castle, CHALLENGE a challenge. Pawns run in the mover's
// direction, whoever they belong to.
//
// ext: [last move's from + 1 (0: nothing to challenge), its to, the piece that moved, what it
// captured, 1 if it was a bluff, castling rights and 50-move count before it, what the last
// challenge found (NOTE_*)]

export const GREY_CASTLE = 1;
export const GREY_CHALLENGE = 2;

const FROM = 0;
const TO = 1;
const PIECE = 2;
const CAPTURED = 3;
const GUILTY = 4;
const CASTLING = 5;
const HALFMOVE = 6;
const NOTE = 7;

export const NOTE_CAUGHT = 1;
export const NOTE_CLEARED = 2;
/** A king was taken with a bluff, which is always caught. */
export const NOTE_KING_SAVED = 3;

export const GREY_FEN = `${STANDARD_FEN} -`;

export interface LastMove {
  from: number;
  to: number;
  piece: number;
  captured: number;
  /** The piece wasn't the mover's. Only the engine and the bot's referee may look. */
  bluff: boolean;
}

/** The move that can be challenged right now, or null. */
export function challengeable(pos: { ext: Int32Array | null }): LastMove | null {
  const ext = pos.ext;
  if (!ext || !ext[FROM]) return null;
  return { from: ext[FROM] - 1, to: ext[TO], piece: ext[PIECE], captured: ext[CAPTURED], bluff: ext[GUILTY] === 1 };
}

/** What the last challenge found (NOTE_*), or 0. */
export function challengeNote(pos: { ext: Int32Array | null }): number {
  return pos.ext?.[NOTE] ?? 0;
}

function slide(b: Int8Array, sq: number, df: number, dr: number, from: number, out: number[], capturesOnly: boolean) {
  let f = (sq & 7) + df;
  let r = (sq >> 3) + dr;
  while (onBoard(f, r)) {
    const to = r * 8 + f;
    const q = b[to];
    if (q === 0) {
      if (!capturesOnly) out.push(encodeMove(from, to, 0, FLAG_ACTION));
    } else {
      if (q & COLOR_MASK) out.push(encodeMove(from, to, 0, FLAG_ACTION | FLAG_CAPTURE));
      return;
    }
    f += df;
    r += dr;
  }
}

/** Moves of the piece on `sq` as if `us` owned it (any piece can be captured, even your own). */
function pieceMoves(pos: Position, sq: number, us: Color, out: number[], capturesOnly: boolean) {
  const b = pos.board;
  const p = b[sq];
  const t = p & TYPE_MASK;
  const def = pieceDef(t);
  if (t === PAWN) {
    const dir = us === WHITE ? 1 : -1;
    const r = sq >> 3;
    const f = sq & 7;
    const r1 = r + dir;
    if (r1 < 0 || r1 > 7) return;
    const lastRow = r1 === (us === WHITE ? 7 : 0);
    const one = r1 * 8 + f;
    if (b[one] === 0 && (!capturesOnly || lastRow)) {
      out.push(encodeMove(sq, one, 0, FLAG_ACTION));
      const start = us === WHITE ? 1 : 6;
      const two = one + dir * 8;
      if (r === start && b[two] === 0 && !capturesOnly) out.push(encodeMove(sq, two, 0, FLAG_ACTION));
    }
    for (const df of [-1, 1]) {
      if (f + df < 0 || f + df > 7) continue;
      const to = r1 * 8 + f + df;
      if (b[to] & COLOR_MASK) out.push(encodeMove(sq, to, 0, FLAG_ACTION | FLAG_CAPTURE));
    }
    return;
  }
  for (const [df, dr] of def.leaps ?? []) {
    const f = (sq & 7) + df;
    const r = (sq >> 3) + dr;
    if (!onBoard(f, r)) continue;
    const to = r * 8 + f;
    const q = b[to];
    if (q === 0) {
      if (!capturesOnly) out.push(encodeMove(sq, to, 0, FLAG_ACTION));
    } else if (q & COLOR_MASK) out.push(encodeMove(sq, to, 0, FLAG_ACTION | FLAG_CAPTURE));
  }
  for (const [df, dr] of def.slides ?? []) slide(b, sq, df, dr, sq, out, capturesOnly);
}

function castles(pos: Position, us: Color, out: number[]) {
  const b = pos.board;
  const home = us === WHITE ? 4 : 60;
  const bit = colorBit(us);
  if (b[home] !== (KING | bit)) return;
  const rook = ROOK | bit;
  const k = us === WHITE ? CASTLE_WK : CASTLE_BK;
  const q = us === WHITE ? CASTLE_WQ : CASTLE_BQ;
  if (pos.castling & k && b[home + 1] === 0 && b[home + 2] === 0 && b[home + 3] === rook) {
    out.push(encodeMove(home, home + 2, GREY_CASTLE, FLAG_ACTION));
  }
  if (pos.castling & q && b[home - 1] === 0 && b[home - 2] === 0 && b[home - 3] === 0 && b[home - 4] === rook) {
    out.push(encodeMove(home, home - 2, GREY_CASTLE, FLAG_ACTION));
  }
}

function findKings(pos: Position) {
  pos.royals = [-1, -1];
  for (let sq = 0; sq < 64; sq++) {
    const p = pos.board[sq];
    if ((p & TYPE_MASK) === KING && p & COLOR_MASK) pos.royals[pieceColor(p) as Color] = sq;
  }
}

/** Takes back the last move (a caught bluff). */
function takeBack(ctx: MoveContext) {
  const { next } = ctx;
  const ext = next.ext!;
  const from = ext[FROM] - 1;
  const to = ext[TO];
  const piece = ext[PIECE];
  next.board[from] = piece;
  next.board[to] = ext[CAPTURED];
  next.castling = ext[CASTLING];
  next.halfmove = ext[HALFMOVE];
  findKings(next);
  if (ctx.events) {
    ctx.events.push({ t: 'move', from: to, to: from, piece });
    if (ext[CAPTURED]) ctx.events.push({ t: 'add', sq: to, piece: ext[CAPTURED], cause: 'takeback' });
  }
  ctx.fx('caught', from);
}

function clearLast(ext: Int32Array) {
  for (let i = FROM; i <= HALFMOVE; i++) ext[i] = 0;
}

function makeGrey(honest: boolean): Variant {
  return {
    id: honest ? 'grey-honest' : 'grey',
    info: {
      name: 'Grey Area Chess',
      but: 'every piece is grey',
      tagline: 'Both armies look exactly the same. Remember which pieces are yours, or don’t, and see if they notice.',
      summary: 'Every piece is **grey**. You can move any piece, even theirs, but they can **challenge**: get caught and you lose your turn.',
      rules: [
        'Both armies are grey. You start as normal, so keep track of which pieces are yours.',
        'You may move any piece, even one of your opponent’s. Pawns always run your way.',
        'Instead of moving, your opponent may challenge your last move. If the piece wasn’t yours, the move is taken back and you lose your turn. If it was, they lose theirs and you go again.',
        'There’s no check: capture the king to win. Taking a king with one of their pieces is always caught.',
        'No en passant. A pawn reaching the far row becomes a queen. Repetition and the 50-move rule draw.',
      ],
      accent: '#a98bff',
      emoji: '🩶',
      bots: ['Goldfish', 'Daydreamer', 'Accountant', 'Detective', 'Elephant'],
    },
    startFen: GREY_FEN,
    checks: false,
    enPassant: false,
    extSize: 8,
    hiddenInfo: true,
    // Not the saved 50-move count: it grows every cycle and would hide repetitions.
    hashExt: (pos) => pos.ext!.subarray(FROM, HALFMOVE),

    parseExtra(fields, pos) {
      // "-", or the challengeable last move: from,to,piece,captured,bluff,castling,halfmove.
      const [last = '-'] = fields;
      if (last === '-') return;
      const v = last.split(',').map(Number);
      if (v.length !== 7 || v.some((n) => !Number.isInteger(n) || n < 0) || v[0] > 63 || v[1] > 63) throw new FenError('Bad last move');
      const ext = pos.ext!;
      ext[FROM] = v[0] + 1;
      ext[TO] = v[1];
      ext[PIECE] = v[2];
      ext[CAPTURED] = v[3];
      ext[GUILTY] = v[4] ? 1 : 0;
      ext[CASTLING] = v[5] & 15;
      ext[HALFMOVE] = v[6];
    },

    formatExtra(pos) {
      const ext = pos.ext!;
      if (!ext[FROM]) return ['-'];
      return [[ext[FROM] - 1, ext[TO], ext[PIECE], ext[CAPTURED], ext[GUILTY], ext[CASTLING], ext[HALFMOVE]].join(',')];
    },

    replaceMoves(pos, out, capturesOnly) {
      const us = pos.turn;
      if (!honest && pos.ext![FROM] && !capturesOnly) out.push(encodeMove(0, 0, GREY_CHALLENGE, FLAG_ACTION));
      for (let sq = 0; sq < 64; sq++) {
        const p = pos.board[sq];
        if (!(p & COLOR_MASK)) continue;
        if (honest && pieceColor(p) !== us) continue;
        pieceMoves(pos, sq, us, out, capturesOnly);
      }
      if (!capturesOnly) castles(pos, us, out);
      return true;
    },

    afterMove(ctx: MoveContext) {
      const { next, prev, us, them, move } = ctx;
      const ext = next.ext!;
      const kind = movePromo(move);
      ext[NOTE] = 0;
      if (kind === GREY_CHALLENGE) {
        next.halfmove = prev.halfmove;
        if (ext[GUILTY]) {
          takeBack(ctx);
          ext[NOTE] = NOTE_CAUGHT;
          next.turn = us;
        } else {
          ctx.fx('cleared', ext[TO]);
          ext[NOTE] = NOTE_CLEARED;
          next.turn = them;
        }
        clearLast(ext);
        return;
      }
      const from = moveFrom(move);
      const to = moveTo(move);
      const piece = next.board[from];
      const captured = next.board[to];
      ext[FROM] = from + 1;
      ext[TO] = to;
      ext[PIECE] = piece;
      ext[CAPTURED] = captured;
      ext[GUILTY] = pieceColor(piece) === us ? 0 : 1;
      ext[CASTLING] = prev.castling;
      ext[HALFMOVE] = prev.halfmove;
      ctx.relocate(from, to, 'capture');
      if ((piece & TYPE_MASK) === PAWN) {
        next.halfmove = 0;
        if (to >> 3 === (us === WHITE ? 7 : 0)) {
          const queen = QUEEN | (piece & COLOR_MASK);
          next.board[to] = queen;
          ctx.events?.push({ t: 'add', sq: to, piece: queen, cause: 'promotion' });
        }
      }
      if (kind === GREY_CASTLE) ctx.relocate(to > from ? from + 3 : from - 4, to > from ? from + 1 : from - 1);
      if ((captured & TYPE_MASK) === KING && ext[GUILTY]) {
        // Nobody would let that go: the bluff is caught on the spot.
        takeBack(ctx);
        ext[NOTE] = NOTE_KING_SAVED;
        clearLast(ext);
      }
    },

    nextTurn(ctx) {
      return ctx.next.turn;
    },

    noMoves() {
      return draw('stalemate');
    },

    actionSan(pos, move) {
      const kind = movePromo(move);
      if (kind === GREY_CHALLENGE) return 'Challenge!';
      const from = moveFrom(move);
      const to = moveTo(move);
      if (kind === GREY_CASTLE) return to > from ? 'O-O' : 'O-O-O';
      const letter = pieceDef(pos.board[from] & TYPE_MASK).letter.toUpperCase();
      return `${letter === 'P' ? '' : letter}${squareName(from)}${pos.board[to] ? 'x' : '–'}${squareName(to)}`;
    },

    botMove: honest ? undefined : (pos, bot) => greyBot(pos, bot),

    reasonText: {
      'king-captured': 'The king was captured',
    },
  };
}

/** The real game. */
export const grey: Variant = makeGrey(false);
/** The same game without bluffs or challenges, for the computer's search. */
export const greyHonest: Variant = makeGrey(true);

/** How often each level challenges a bluff, and challenges an honest move by mistake. */
const CATCH = [0.3, 0.5, 0.7, 0.85, 1];
const FALSE_ALARM = [0.06, 0.04, 0.02, 0.01, 0];

function greyBot(pos: Position, bot: { level: number; random: () => number; search(v: Variant, p: Position): number | null }): number | null {
  // The computer remembers every piece (that's its whole advantage), but weaker levels
  // forget to check. It never bluffs.
  const last = challengeable(pos);
  if (last) {
    const i = Math.max(0, Math.min(4, bot.level - 1));
    const r = bot.random();
    if (last.bluff ? r < CATCH[i] : r < FALSE_ALARM[i]) return encodeMove(0, 0, GREY_CHALLENGE, FLAG_ACTION);
  }
  return bot.search(greyHonest, pos);
}
